import { useState, useEffect, useMemo } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  Box,
  Typography,
  Paper,
  Button,
  Autocomplete,
  TextField,
  Checkbox,
  FormControl,
  FormGroup,
  FormControlLabel,
  CircularProgress,
  Alert,
  Grid,
  Chip,
} from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { chartDateAxis, formatChartDate, niceAxis } from "../../utils/chart";
import { dateFormat } from "../AvailableDatePicker";
import { DatePicker } from "rsuite";
import { CustomProvider } from "rsuite";
import arEG from "../../i18n/rsuiteArabic";
import { useTranslation } from "react-i18next";
import i18n, { useLocalize } from "../../i18n";
import { toast } from "react-toastify";
import { getChartsData, getFieldsDashboard } from "../../api/FlaskAPI";
import { chartKeys, fieldKeys } from "../../api/queryKeys";
import "./DashboardPage.css";
import { useSelector } from "react-redux";
import "rsuite/dist/rsuite.min.css";

const ownerName = (field) => field.owner || i18n.t("area.unknown");
const fieldLabel = (field) => `${ownerName(field)} (${field.id})`;

const FieldName = ({ field }) => (
  <span>
    <span style={{ fontWeight: 600 }}>{ownerName(field)}</span>{" "}
    <span style={{ fontSize: 11, color: "#6b7280" }}>{field.id}</span>
  </span>
);
const capitalize = (text) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : "";

// Hover box
const ChartTooltip = ({ active, payload, label, fields }) => {
  if (!active || !payload?.length) return null;
  return (
    <Box
      dir={i18n.dir()}
      sx={{
        p: 1.25,
        bgcolor: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 2,
        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
      }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>
        {formatChartDate(label)}
      </Typography>
      {payload.map((item) => {
        const field = fields.find((f) => f.id === item.dataKey);
        return (
          <Box
            key={item.dataKey}
            sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 12 }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: item.color,
              }}
            />
            {field ? <FieldName field={field} /> : item.dataKey}
            <Box component="span" sx={{ ml: "auto", pl: 1.5, fontWeight: 600 }}>
              {item.value == null
                ? "—"
                : Number(item.value).toLocaleString("en-US", {
                    maximumFractionDigits: 6,
                  })}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

// Colors for different fields in charts
const FIELD_COLORS = [
  "#19466B",
  "#f44336",
  "#4caf50",
  "#ff9800",
  "#9c27b0",
  "#00bcd4",
  "#e91e63",
  "#795548",
  "#607d8b",
  "#3f51b5",
];

const ChartsPage = () => {
  // Redux state
  const indices = useSelector((state) => state.indices || []);
  const {
    t,
    i18n: { resolvedLanguage: language },
  } = useTranslation();
  const localize = useLocalize();

  // Local state
  const [selectedFields, setSelectedFields] = useState([]);
  const [selectedIndices, setSelectedIndices] = useState([]);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  // What "Generate" was last clicked with.
  const [submitted, setSubmitted] = useState(null);
  const [fieldSearchQuery, setFieldSearchQuery] = useState("");
  const [debouncedFieldSearch, setDebouncedFieldSearch] = useState("");

  // Debounce field search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFieldSearch(fieldSearchQuery);
    }, 400);
    return () => clearTimeout(timer);
  }, [fieldSearchQuery]);

  // Shares `fieldKeys` with the Fields table, so both use one cache entry.
  const fieldParams = useMemo(() => {
    const params = { page: 1, per_page: 10 };
    if (debouncedFieldSearch.trim())
      params.search = debouncedFieldSearch.trim();
    return params;
  }, [debouncedFieldSearch]);

  const { data: fieldsData, isFetching: fetchingFields } = useQuery({
    queryKey: fieldKeys.list(fieldParams),
    queryFn: ({ signal }) =>
      getFieldsDashboard(fieldParams, signal).then((res) => res.data),
    placeholderData: keepPreviousData,
    meta: { errorMessage: t("chartsPage.toasts.fieldsFailed") },
  });

  const fetchedFields = useMemo(() => fieldsData?.items || [], [fieldsData]);

  // Already-picked fields stay at the top of the list so they don't disappear
  // when the server returns a page that no longer contains them.
  const fieldOptions = useMemo(() => {
    const selectedIds = new Set(selectedFields.map((f) => f.id));
    return [
      ...selectedFields,
      ...fetchedFields.filter((f) => !selectedIds.has(f.id)),
    ];
  }, [selectedFields, fetchedFields]);

  // Filter out RGB indices
  const availableIndices = useMemo(() => {
    return indices.filter((idx) => idx.use_stats);
  }, [indices]);

  // Convert date string to Date object (avoiding timezone issues)
  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    const [year, month, day] = dateStr.split("-").map(Number);
    // Month is 0-indexed in JavaScript Date
    return new Date(year, month - 1, day);
  };

  // Calculate date constraints based on selected indices
  const { minDate, maxDate } = useMemo(() => {
    if (selectedIndices.length === 0) return { minDate: null, maxDate: null };

    const allDates = availableIndices
      .filter((idx) => selectedIndices.includes(idx.id))
      .flatMap((idx) => idx.tifs_dates || []);

    if (allDates.length === 0) return { minDate: null, maxDate: null };

    allDates.sort();

    return {
      minDate: parseDate(allDates[0]),
      maxDate: parseDate(allDates[allDates.length - 1]),
    };
  }, [selectedIndices, availableIndices]);

  // Defaults the empty date boxes. Was a setState during render, in the
  // useMemo above.
  useEffect(() => {
    if (minDate && !fromDate) setFromDate(minDate);
    if (maxDate && !toDate) setToDate(maxDate);
  }, [minDate, maxDate, fromDate, toDate]);

  // Handle index selection toggle
  const handleIndexToggle = (indexId) => {
    setSelectedIndices((prev) =>
      prev.includes(indexId)
        ? prev.filter((id) => id !== indexId)
        : [...prev, indexId],
    );
  };

  // Convert Date object to string (YYYY-MM-DD) - using local date to avoid timezone issues
  const formatDateToString = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Validate date range
  const isDateValid = (date, minDate, maxDate) => {
    if (!date || !minDate || !maxDate) return false;
    return date >= minDate && date <= maxDate;
  };

  // Generate charts
  const handleGenerateCharts = () => {
    if (selectedFields.length === 0) {
      toast.error(t("chartsPage.toasts.selectField"));
      return;
    }
    if (selectedIndices.length === 0) {
      toast.error(t("chartsPage.toasts.selectIndex"));
      return;
    }
    if (!fromDate || !toDate) {
      toast.error(t("chartsPage.toasts.selectDates"));
      return;
    }
    if (fromDate > toDate) {
      toast.error(t("chartsPage.toasts.fromAfterTo"));
      return;
    }

    setSubmitted({
      params: {
        fields_ids: selectedFields.map((f) => f.id),
        indices_ids: selectedIndices,
        from_date: formatDateToString(fromDate),
        to_date: formatDateToString(toDate),
      },
      // The rendered legend needs the fields as they were at submit time.
      fields: selectedFields,
    });
  };

  // A query, not a mutation: this POST only reads. Keyed on the submitted
  // selection, so re-generating the same one is free.
  const {
    data: chartsResult,
    isFetching: loading,
    isError,
    error: chartsError,
  } = useQuery({
    queryKey: chartKeys.data(submitted?.params),
    queryFn: ({ signal }) =>
      getChartsData(submitted.params, signal).then((res) => res.data),
    enabled: Boolean(submitted),
  });

  const error = isError
    ? chartsError?.response?.data?.message ||
      t("chartsPage.toasts.generateFailed")
    : null;

  const chartData =
    submitted && chartsResult
      ? { selectedFields: submitted.fields, data: chartsResult }
      : null;

  return (
    <div className="dashboard-page">
      <div className="dashboard-header">
        <div>
          <h1>{t("chartsPage.title")}</h1>
          <p>{t("chartsPage.subtitle")}</p>
        </div>
      </div>

      {/* Filters Section */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={3}>
          {/* Fields Selector */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              {t("chartsPage.selectFields")}
            </Typography>
            <Autocomplete
              multiple
              options={fieldOptions}
              getOptionLabel={fieldLabel}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {ownerName(option)}
                    </Typography>
                    <Typography
                      variant="caption"
                      noWrap
                      sx={{ display: "block", color: "text.secondary" }}
                    >
                      {[
                        capitalize(localize(option, "crop")),
                        t("chartsPage.idLabel", { id: option.id }),
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </Typography>
                  </Box>
                </li>
              )}
              value={selectedFields}
              onChange={(_, newValue) => {
                setSelectedFields(newValue);
              }}
              onInputChange={(_, inputValue, reason) => {
                if (reason === "input" || reason === "clear") {
                  setFieldSearchQuery(inputValue || "");
                }
              }}
              loading={fetchingFields}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    label={fieldLabel(option)}
                    size="small"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={
                    selectedFields.length === 0
                      ? t("chartsPage.searchFields")
                      : ""
                  }
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {fetchingFields ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          {/* Indices Selector */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              {t("chartsPage.selectIndices")}
            </Typography>
            <FormControl component="fieldset" fullWidth>
              <FormGroup row>
                {availableIndices.map((index) => (
                  <FormControlLabel
                    key={index.id}
                    control={
                      <Checkbox
                        checked={selectedIndices.includes(index.id)}
                        onChange={() => handleIndexToggle(index.id)}
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="body2">
                        {localize(index, "short_name")}
                      </Typography>
                    }
                  />
                ))}
              </FormGroup>
            </FormControl>
            {minDate && maxDate && (
              <Typography
                component="span"
                variant="caption"
                sx={{ ml: 1, color: "text.secondary" }}
              >
                {t("chartsPage.available", {
                  from: formatDateToString(minDate),
                  to: formatDateToString(maxDate),
                })}
              </Typography>
            )}
          </Grid>

          {/* Date Range */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t("chartsPage.fromDate")}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                disabled={!minDate}
                onClick={() => setFromDate(minDate)}
                sx={{ ml: 1, textTransform: "none", py: 0, minWidth: 0 }}
              >
                {t("chartsPage.resetMin")}
              </Button>
            </Box>
            <CustomProvider
              locale={language === "ar" ? arEG : undefined}
              rtl={language === "ar"}
            >
              <DatePicker
                key={`from-${formatDateToString(minDate) || "empty"}`}
                value={fromDate}
                defaultValue={fromDate ? fromDate : null}
                onChange={(date) => setFromDate(date)}
                disabled={!minDate}
                cleanable={false}
                shouldDisableDate={(date) => {
                  return date < minDate || date > toDate;
                }}
                style={{ width: "100%" }}
                format={dateFormat(language)}
                editable={false}
                placeholder={t("chartsPage.startPlaceholder")}
              />
            </CustomProvider>
            {fromDate &&
              minDate &&
              maxDate &&
              !isDateValid(fromDate, minDate, maxDate) && (
                <Typography
                  variant="caption"
                  sx={{ color: "error.main", mt: 0.5, display: "block" }}
                >
                  {t("chartsPage.between", {
                    from: formatDateToString(minDate),
                    to: formatDateToString(maxDate),
                  })}
                </Typography>
              )}
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t("chartsPage.toDate")}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                disabled={!maxDate}
                onClick={() => setToDate(maxDate)}
                sx={{ ml: 1, textTransform: "none", py: 0, minWidth: 0 }}
              >
                {t("chartsPage.resetMax")}
              </Button>
            </Box>
            <CustomProvider
              locale={language === "ar" ? arEG : undefined}
              rtl={language === "ar"}
            >
              <DatePicker
                key={`to-${formatDateToString(maxDate) || "empty"}`}
                value={toDate}
                defaultValue={toDate ? toDate : null}
                onChange={(date) => setToDate(date)}
                disabled={!maxDate}
                cleanable={false}
                shouldDisableDate={(date) => {
                  return date < fromDate || date > maxDate;
                }}
                style={{ width: "100%" }}
                format={dateFormat(language)}
                editable={false}
                placeholder={t("chartsPage.endPlaceholder")}
              />
            </CustomProvider>
            {toDate &&
              minDate &&
              maxDate &&
              !isDateValid(toDate, minDate, maxDate) && (
                <Typography
                  variant="caption"
                  sx={{ color: "error.main", mt: 0.5, display: "block" }}
                >
                  {t("chartsPage.between", {
                    from: formatDateToString(minDate),
                    to: formatDateToString(maxDate),
                  })}
                </Typography>
              )}
          </Grid>

          {/* Generate Button */}
          <Grid item xs={12}>
            <Button
              variant="contained"
              onClick={handleGenerateCharts}
              disabled={
                loading ||
                selectedFields.length === 0 ||
                selectedIndices.length === 0 ||
                !fromDate ||
                !toDate ||
                !isDateValid(fromDate, minDate, maxDate) ||
                !isDateValid(toDate, minDate, maxDate)
              }
              sx={{
                backgroundColor: "primary.main",
                textTransform: "none",
                fontWeight: 600,
                px: 4,
                py: 1,
                borderRadius: 2,
                "&:hover": {
                  backgroundColor: "primary.dark",
                },
              }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: "white" }} />
              ) : (
                t("chartsPage.generate")
              )}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Charts Section */}
      {chartData?.data && chartData.data.length > 0 && (
        <Box>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            {t("chartsPage.results")}
          </Typography>

          <Grid container spacing={3}>
            {chartData.data.map((indexData, indexIdx) => {
              const info = indices.find(
                (index) => index.short_name_en === indexData.name_en,
              );
              const unit = localize(indexData, "unit") || null;
              return (
                <Grid item xs={12} key={indexData.id || indexIdx}>
                  <Paper sx={{ p: 3, borderRadius: 2 }}>
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: "#374151",
                          lineHeight: 1.3,
                        }}
                      >
                        {localize(indexData, "name")}
                      </Typography>
                      {info?.full_name_en && (
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary" }}
                        >
                          {localize(info, "full_name")}
                        </Typography>
                      )}
                    </Box>

                    {/* Charts read left to right, also in Arabic (time axis). */}
                    <Box dir="ltr" sx={{ height: 400, width: "100%" }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                          data={indexData.values}
                          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="#e5e7eb"
                          />
                          <XAxis
                            dataKey="date"
                            tick={{ fontSize: 10 }}
                            {...chartDateAxis()}
                            interval="preserveStartEnd"
                          />
                          <YAxis
                            tick={{ fontSize: 12 }}
                            label={
                              unit && {
                                value: unit,
                                angle: -90,
                                position: "insideLeft",
                                style: { fontSize: 12, fill: "#6b7280" },
                              }
                            }
                            {...niceAxis(indexData.min_y, indexData.max_y)}
                          />
                          <Tooltip
                            content={
                              <ChartTooltip fields={chartData.selectedFields} />
                            }
                          />
                          <Legend
                            formatter={(value) => {
                              const field = chartData.selectedFields.find(
                                (f) => f.id === value,
                              );
                              return field ? (
                                <FieldName field={field} />
                              ) : (
                                value
                              );
                            }}
                          />
                          {chartData.selectedFields.map((field, fieldIdx) => (
                            <Line
                              key={field.id}
                              type="monotone"
                              dataKey={field.id}
                              stroke={
                                FIELD_COLORS[fieldIdx % FIELD_COLORS.length]
                              }
                              strokeWidth={2}
                              dot={false}
                              activeDot={{ r: 5 }}
                              connectNulls
                            />
                          ))}
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {/* Empty data State */}
      {chartData?.data && chartData.data.length === 0 && (
        <Paper
          sx={{
            p: 4,
            textAlign: "center",
            backgroundColor: "#f9fafb",
            borderRadius: 2,
            border: "2px dashed #e5e7eb",
          }}
        >
          <Typography variant="h6" sx={{ color: "#d32f2f", mb: 1 }}>
            {t("chartsPage.noDataTitle")}
          </Typography>
          <Typography variant="body2" sx={{ color: "#9ca3af" }}>
            {t("chartsPage.noDataBody")}
          </Typography>
        </Paper>
      )}

      {/* Empty State */}
      {!chartData && !loading && !error && (
        <Paper
          sx={{
            p: 4,
            textAlign: "center",
            backgroundColor: "#f9fafb",
            borderRadius: 2,
            border: "2px dashed #e5e7eb",
          }}
        >
          <Typography variant="h6" sx={{ color: "#6b7280", mb: 1 }}>
            {t("chartsPage.emptyTitle")}
          </Typography>
          <Typography variant="body2" sx={{ color: "#9ca3af" }}>
            {t("chartsPage.emptyBody")}
          </Typography>
        </Paper>
      )}
    </div>
  );
};

export default ChartsPage;
